import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useInfiniteQuery } from "@tanstack/react-query";

import {
  getAllDealsV2,
  getGetAllDealsV2QueryKey,
  useGetDealGeneralPartnerViewV3,
} from "@vega-api/generated/deal/deal";
import { type DealResponseV2 } from "@vega-api/generated/types/dealResponseV2";
import { SearchableDropdown } from "@vega-core/components/Form/Controls/SearchableDropdown/SearchableDropdown";
import { useReplaceRouteId } from "@vega-core/hooks/useReplaceRouteId";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { SEARCH_DEBOUNCE_DELAY_MS } from "../../constants/searchConstants";

const DEALS_DROPDOWN_PAGE_SIZE = 50;

const dealToOption = (deal: DealResponseV2): OptionType => ({
  label: deal.name || "-",
  value: deal.id,
});

export const CoInvestDealsDropdown = () => {
  const { id: dealId = "" } = useParams<{ id: string }>();
  const { replaceId } = useReplaceRouteId();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    SEARCH_DEBOUNCE_DELAY_MS,
  );
  const [selectedOption, setSelectedOption] = useState<OptionType>();

  const {
    data: dealsPages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [getGetAllDealsV2QueryKey(), debouncedSearchQuery],
    queryFn: ({ pageParam }) =>
      getAllDealsV2({
        page: pageParam,
        pageSize: DEALS_DROPDOWN_PAGE_SIZE,
        searchQuery: debouncedSearchQuery || undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.meta?.hasNextPage ? lastPageParam + 1 : undefined,
  });

  const { data: gpViewData } = useGetDealGeneralPartnerViewV3(dealId);

  const deals: DealResponseV2[] =
    dealsPages?.pages.flatMap((page) => page.data ?? []) ?? [];
  const currentDeal = gpViewData?.data?.deal;

  useEffect(() => {
    if (currentDeal) setSelectedOption(dealToOption(currentDeal));
  }, [dealId, currentDeal]);

  const dealOptions = deals.map(dealToOption);
  const options = selectedOption
    ? [
        { ...selectedOption, disabled: true },
        ...dealOptions.filter((o) => o.value !== selectedOption.value),
      ]
    : dealOptions;

  return (
    <SearchableDropdown
      className="w-full sm:w-80"
      hasNextPage={hasNextPage}
      inputValue={searchQuery}
      isLoading={isLoading}
      isLoadingMore={isFetchingNextPage}
      name="coinvestDealsDropdown"
      onChange={(option) => {
        setSelectedOption(option);

        if (option?.value)
          replaceId(
            CoInvestmentFullPaths.CoInvestments.replace("/", ""),
            option.value,
          );
      }}
      onInputChange={setSearchQuery}
      onLoadMore={fetchNextPage}
      options={options}
      placeholder={m.common_select()}
      shouldFilter={false}
      value={selectedOption}
      withCheckmark
      withChevron
      wrapOptionLabels
    />
  );
};
